/** @file restPlacementOf: where every part of a puppet's rest pose lands on screen in one facing. */
import { classify } from '@zilo/pose3d-facet/classify';
import type { FacetPick } from '@zilo/pose3d-facet/contract';
import { slotKey } from '@zilo/pose3d-format/derive/slotKey';
import { effectiveProfile } from '@zilo/pose3d-format/profile/effectiveProfile';
import { BUILTIN_PROFILES } from '@zilo/pose3d-format/tables/builtinProfiles';
import type { Profile } from '@zilo/pose3d-format/types/profile';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import { evaluate } from '@zilo/pose3d-pose/evaluate';
import { POSE_LAYOUT as P } from '@zilo/pose3d-pose/layout/poseLayout';
import { screenOf } from '@zilo/pose3d-render-core/camera/screenOf';
import { viewOf } from '@zilo/pose3d-render-core/camera/viewOf';
import type { Camera } from '@zilo/pose3d-render-core/contract';
import { corners } from '@zilo/pose3d-render-core/corners/corners';
import { IMPOSTOR_LAYOUT as L } from '@zilo/pose3d-render-core/layout/impostorLayout';
import { place } from '@zilo/pose3d-render-core/place/place';
import type { PlacedPart, Point, RestPlacement } from './sheetTypes';

/**
 * The puppet with a stand-in for every sheet a facet names but `sheets` lacks (placement only reads the name); with
 * `unhide`, no node is hidden (a stowed weapon shows in its hand).
 */
function withSheets(puppet: Puppet, unhide: boolean): Puppet {
  const sheets = { ...puppet.sheets };
  for (const d of puppet.drawables) {
    if (d.kind !== 'impostor') continue;
    for (const e of d.facets) sheets[e.sheet] ??= { image: 'img_placeholder', size: [1, 1] };
  }
  if (!unhide) return { ...puppet, sheets };
  const nodes = Object.fromEntries(Object.entries(puppet.nodes).map(([name, n]) => [name, { ...n, hidden: false }]));
  return { ...puppet, sheets, nodes };
}

/** Every pick that would draw a tile is drawn, whether its slot holds an image or not (a guide shows empty slots). */
function drawEmpty(picks: FacetPick[]): void {
  for (const p of picks) if (!p.hidden && p.entry >= 0 && p.tile >= 0) p.missing = false;
}

/** What `partsOf` reads besides the list. */
type PartsInput = { puppet: Puppet; profile: Profile; list: Float64Array; count: number; picks: FacetPick[]; camera: Camera };

/** The placed parts of the list's `count` entries, far to near, untranslated. */
function partsOf({ puppet, profile, list, count, picks, camera }: PartsInput): PlacedPart[] {
  const view = viewOf(camera, profile);
  const parts: PlacedPart[] = [];
  for (let i = 0; i < count; i++) {
    const at = (k: number): number => list[i * L.stride + k] ?? 0;
    const drawable = puppet.drawables[at(L.drawable)];
    const pick = picks.find((p) => p.drawable === drawable?.id);
    if (drawable?.kind !== 'impostor' || pick === undefined) continue;
    const c = corners(list, i, camera, profile);
    parts.push({
      drawable: drawable.id, node: drawable.node, slot: slotKey(drawable.id, pick.entry, pick.tile),
      size: drawable.facets[pick.entry]?.size ?? [c.tex[2], c.tex[3]], pivot: pick.pivot, flip: (at(L.flags) & 1) !== 0,
      quad: c.corners, pivotScreen: screenOf([at(L.ax), at(L.ay), at(L.az)], view),
      depth: c.depths.reduce((a, b) => a + b, 0) / 4, attachment: /^att\d+\./.test(drawable.id),
    });
  }
  return parts.sort((a, b) => b.depth - a.depth);
}

/** Moves a point by `d`. */
const shift = (p: Point, d: Point): Point => [p[0] + d[0], p[1] + d[1]];

/** The parts and joints moved so their box starts `margin` px in, and the size that holds them with `margin` around. */
function framed(parts: PlacedPart[], joints: { node: string; at: Point }[], margin: number): Omit<RestPlacement, 'facingDeg' | 'sampling'> {
  const points = [...parts.flatMap((p) => p.quad), ...joints.filter((j) => parts.some((p) => p.node === j.node)).map((j) => j.at)];
  const xs = points.map((p) => p[0]);
  const ys = points.map((p) => p[1]);
  const d: Point = [margin - Math.floor(Math.min(...xs)), margin - Math.floor(Math.min(...ys))];
  return {
    width: Math.ceil(Math.max(...xs) + d[0]) + margin, height: Math.ceil(Math.max(...ys) + d[1]) + margin,
    parts: parts.map((p) => ({ ...p, quad: p.quad.map((q) => shift(q, d)) as PlacedPart['quad'], pivotScreen: shift(p.pivotScreen, d) })),
    joints: joints.map((j) => ({ node: j.node, at: shift(j.at, d) })),
  };
}

/**
 * The rest pose (`evaluate` with no clip) at body facing `facingDeg` under the profile camera (yaw 0, profile pitch),
 * classified, placed and cornered like the viewport does, empty slots included. Coordinates are guide pixels (one per
 * texel), shifted so the figure's box starts `margin` px from the top-left. `withAttachments` also draws hidden nodes
 * and the first attachment's weapon group (where a held weapon would be). Throws for an unknown profile.
 */
export function restPlacementOf(source: Puppet, facingDeg: number, withAttachments = false, margin = 4): RestPlacement {
  const puppet = withSheets(source, withAttachments);
  const profile = effectiveProfile(puppet, BUILTIN_PROFILES);
  const names = Object.keys(puppet.nodes);
  const group = withAttachments ? puppet.attachments?.find((a) => a.weaponGroup !== undefined)?.weaponGroup : undefined;
  const params = { bodyFacingDeg: facingDeg, profile, ...(group === undefined ? {} : { weaponGroup: group }) };
  const pose = evaluate(puppet, null, params, new Float64Array(names.length * P.stride));
  const camera: Camera = { kind: 'ortho', yawDeg: 0, pitchDeg: profile.camera.pitch, pixelsPerUnit: profile.pixelsPerUnit, viewport: [1024, 1024], target: [0, 0, 0] };
  const picks = classify(puppet, pose, { yawDeg: 0, pitchDeg: camera.pitchDeg }, params, []);
  drawEmpty(picks);
  const list = new Float64Array(Math.max(1, puppet.drawables.length) * L.stride);
  const count = place(puppet, pose, picks, camera, profile, [0, 0, 0], list);
  const view = viewOf(camera, profile);
  const at = (i: number, k: number): number => pose[i * P.stride + k] ?? 0;
  const joints = names.map((node, i) => ({ node, at: screenOf([at(i, P.px), at(i, P.py), at(i, P.pz)], view) }));
  const parts = partsOf({ puppet, profile, list, count, picks, camera });
  return { facingDeg, sampling: profile.sampling, ...framed(parts, joints, margin) };
}
